// src/components/artwork/forms/PosthumousField.jsx
// Production posthume et frais de production d'une œuvre : qui a autorisé, quand, ce que cela a coûté, où sont les pièces.
// Proposé aux comptes galerie (comme le statut patrimonial) ; visible dès qu'une valeur est saisie, et pour une personne qui
// édite l'œuvre d'un autre compte. Information saisie : ne qualifie ni le caractère original d'une œuvre ni aucun droit.
import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { POSTHUMOUS_KEYS } from "../../../utils/posthumous";

const inputCls = "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3";
const labelCls = "block text-xs font-medium text-gray-700";

export default function PosthumousField({ formData, setFormData, readOnly = false }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const set = (name, value) => setFormData((prev) => ({ ...(prev || {}), [name]: value }));
  const anyValue = POSTHUMOUS_KEYS.some((k) => formData?.[k] !== null && formData?.[k] !== undefined && formData?.[k] !== "");
  const editsForSomeoneElse = !!(formData?.client_id && profile?.id && formData.client_id !== profile.id);
  if (!(profile?.gallery_mode === true || anyValue || editsForSomeoneElse)) return null;

  const flag = formData?.posthumous_production === true ? "yes" : formData?.posthumous_production === false ? "no" : "";
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="text-sm font-semibold text-gray-800">
        {t("artworkEdit.posthumous.title", { defaultValue: "Production posthume et frais de production" })}
      </div>
      <div>
        <label htmlFor="posthumous_production" className={labelCls}>
          {t("artworkEdit.posthumous.flag", { defaultValue: "Œuvre produite après la mort de l'artiste (fonte, tirage, recréation)" })}
        </label>
        <select id="posthumous_production" value={flag} disabled={readOnly} className={inputCls}
          onChange={(e) => set("posthumous_production", e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}>
          <option value="">{t("artworkEdit.posthumous.unspecified", { defaultValue: "Non précisé" })}</option>
          <option value="yes">{t("artworkEdit.posthumous.yes", { defaultValue: "Oui" })}</option>
          <option value="no">{t("artworkEdit.posthumous.no", { defaultValue: "Non" })}</option>
        </select>
      </div>
      {flag === "yes" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="posthumous_production_year" className={labelCls}>{t("artworkEdit.posthumous.year", { defaultValue: "Année de production" })}</label>
            <input id="posthumous_production_year" type="number" min="1800" max="2200" step="1" inputMode="numeric" disabled={readOnly}
              value={formData?.posthumous_production_year ?? ""} onChange={(e) => set("posthumous_production_year", e.target.value || null)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="posthumous_authorized_by" className={labelCls}>{t("artworkEdit.posthumous.authorizedBy", { defaultValue: "Autorisée par" })}</label>
            <input id="posthumous_authorized_by" type="text" maxLength={300} disabled={readOnly} value={formData?.posthumous_authorized_by || ""}
              placeholder={t("artworkEdit.posthumous.authorizedByPlaceholder", { defaultValue: "Estate, ayant droit, fondation" })}
              onChange={(e) => set("posthumous_authorized_by", e.target.value)} className={inputCls} />
          </div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="production_cost" className={labelCls}>{t("artworkEdit.posthumous.cost", { defaultValue: "Frais de production" })}</label>
          <input id="production_cost" type="number" min="0" step="0.01" inputMode="decimal" disabled={readOnly}
            value={formData?.production_cost ?? ""} onChange={(e) => set("production_cost", e.target.value || null)} className={inputCls} />
        </div>
        <div>
          <label htmlFor="production_cost_currency" className={labelCls}>{t("artworkEdit.posthumous.currency", { defaultValue: "Devise" })}</label>
          <input id="production_cost_currency" type="text" maxLength={3} disabled={readOnly} value={formData?.production_cost_currency || ""}
            placeholder="EUR" onChange={(e) => set("production_cost_currency", e.target.value.toUpperCase())} className={inputCls} />
        </div>
      </div>
      <div>
        <label htmlFor="production_notes" className={labelCls}>{t("artworkEdit.posthumous.notes", { defaultValue: "Où sont les pièces" })}</label>
        <textarea id="production_notes" rows={2} maxLength={2000} disabled={readOnly} value={formData?.production_notes || ""}
          placeholder={t("artworkEdit.posthumous.notesPlaceholder", { defaultValue: "Fondeur, atelier d'impression, tirage, certificat : où se trouvent les documents" })}
          onChange={(e) => set("production_notes", e.target.value)} className={inputCls} />
      </div>
      <p className="text-xs text-gray-500">
        {t("artworkEdit.posthumous.hint", { defaultValue: "Information saisie par vous : ARTICHAUT ne qualifie ni le caractère original d'une œuvre ni aucun droit." })}
      </p>
    </div>
  );
}
